import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Lock, ShieldCheck } from 'lucide-react';
import { authSetPassword, authResetPassword } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';

// Shared page used for BOTH /set-password (invite flow) and /reset-password (forgot flow).
// Distinguished by props.mode = 'invite' | 'reset'
const SetPasswordPage = ({ mode = 'invite' }) => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const copy = useMemo(() => (
    mode === 'reset'
      ? { title: 'Reset your password', intro: 'Choose a new password for your Emerald Corporates account.', cta: 'Reset password', missing: 'This reset link is missing or invalid. Please request a new one from the sign-in page.' }
      : { title: 'Set your password', intro: 'Welcome to Emerald Corporates. Choose a password to complete your account setup.', cta: 'Set password &amp; sign in', missing: 'This invite link is missing or invalid. Please ask your administrator to resend it.' }
  ), [mode]);

  const strong = pw.length >= 8;
  const match = pw && pw === pw2;

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!strong) { toast({ title: 'Password too short', description: 'Use at least 8 characters.', variant: 'destructive' }); return; }
    if (!match) { toast({ title: 'Passwords don\'t match', variant: 'destructive' }); return; }
    setLoading(true);
    try {
      if (mode === 'reset') await authResetPassword(token, pw);
      else await authSetPassword(token, pw);
      await refresh();
      toast({ title: 'All set', description: mode === 'reset' ? 'Password reset — you are signed in.' : 'Welcome to Emerald.' });
      navigate('/admin', { replace: true });
    } catch (err) {
      toast({ title: 'Could not save password', description: err?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#e2ede6] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center mb-6">
          <span className="text-[#0f5c3d] leading-none" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '52px', fontWeight: 500 }}>E</span>
        </Link>
        <div className="bg-white border border-black/5 rounded-2xl shadow-sm p-6 lg:p-8" data-testid={`${mode}-password-page`}>
          <h1 className="text-3xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>{copy.title}</h1>
          <p className="mt-1 text-sm text-[#0b1a2e]/60">{copy.intro}</p>

          {!token ? (
            <div className="mt-6 bg-red-50 border border-red-100 rounded-lg p-4 text-sm text-red-700">{copy.missing}</div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <label className="block">
                <span className="block text-xs font-semibold tracking-wide uppercase text-[#0b1a2e]/70 mb-1.5">New password</span>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b1a2e]/50" />
                  <input
                    required type="password" value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    onFocus={() => setShowRules(true)}
                    autoComplete="new-password" data-testid="password-new"
                    className="w-full border border-[#0f5c3d]/15 rounded-lg pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30"
                  />
                </div>
              </label>
              <label className="block">
                <span className="block text-xs font-semibold tracking-wide uppercase text-[#0b1a2e]/70 mb-1.5">Confirm password</span>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b1a2e]/50" />
                  <input
                    required type="password" value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    autoComplete="new-password" data-testid="password-confirm"
                    className="w-full border border-[#0f5c3d]/15 rounded-lg pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30"
                  />
                </div>
              </label>
              {showRules && (
                <ul className="text-xs space-y-1">
                  <li className={strong ? 'text-emerald-700' : 'text-[#0b1a2e]/50'}>{strong ? '✓' : '•'} At least 8 characters</li>
                  <li className={match ? 'text-emerald-700' : 'text-[#0b1a2e]/50'}>{match ? '✓' : '•'} Passwords match</li>
                </ul>
              )}
              <button
                type="submit" disabled={loading || !strong || !match} data-testid="submit-password"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#0f5c3d] text-white font-medium text-sm hover:bg-[#0b1a2e] transition disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                <span dangerouslySetInnerHTML={{ __html: copy.cta }} />
              </button>
            </form>
          )}
        </div>
        <p className="mt-6 text-center text-xs text-[#0b1a2e]/50">
          <Link to="/login" className="hover:text-[#0f5c3d]">← Back to sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default SetPasswordPage;
